import { createProvider } from '@frontend/store';
import { PX_PER_MINUTE } from '../constants';
import {
  CalendarColumn,
  CalendarColumnGroup,
  CalendarEvent,
  CalendarColumnHeaderType,
  CalendarTimeIndicatorLines,
  CalendarColumnBodyVariant,
  CalendarGridDragCreateSelection,
} from '../types';

type State = {
  headerType: CalendarColumnHeaderType;
  columnBodyVariant: CalendarColumnBodyVariant;
  groupIds: string[]; // keep this to maintain group order as we can't rely on object key order
  groupsById: Record<string, CalendarColumnGroup | undefined>;
  columnsById: Record<string, CalendarColumn | undefined>;
  eventsById: Record<string, CalendarEvent | undefined>;

  // derived state
  hasMultipleGroups: boolean; // can be used to determine if we need to show group headers, and also for some styling decisions
  canScrollLeft: boolean;
  canScrollRight: boolean;

  // other state
  pxPerMinute: number;
  timeIndicatorLines: CalendarTimeIndicatorLines;
  dragCreateSelection?: CalendarGridDragCreateSelection;

  // column reorder state
  isColumnReorderEnabled: boolean;
  isColumnReorderDragging: boolean;
  localColumnOrderByGroup: Record<string, string[] | undefined>;
  onColumnReorder?: (groupId: string, newColumnIds: string[]) => void;
  groupColumnWidth: number;
  selectedEventId?: string;
  onEventSelect?: (eventId: string) => void;
};

type Actions = {
  setPxPerMinute: (px: number) => void;
  setTimeIndicatorLines: (interval: CalendarTimeIndicatorLines) => void;
  setDragCreateSelection: (selection?: CalendarGridDragCreateSelection) => void;
  setLocalColumnOrder: (groupId: string, columnIds: string[]) => void;
  clearLocalColumnOrder: (groupId: string) => void;
  setIsColumnReorderDragging: (dragging: boolean) => void;
  setGroupColumnWidth: (width: number) => void;
  setSelectedEventId: (eventId?: string) => void;
};

export type CalendarGridStore = State & Actions;

export const {
  Provider: CalendarGridStoreProvider,
  useStore: useCalendarGridStore,
  useNonReactiveStore,
} = createProvider<CalendarGridStore>()((set) => ({
  headerType: 'default',
  columnBodyVariant: 'grid',
  groupIds: [],
  groupsById: {},
  columnsById: {},
  eventsById: {},

  hasMultipleGroups: false,
  canScrollLeft: false,
  canScrollRight: false,

  pxPerMinute: PX_PER_MINUTE,
  timeIndicatorLines: 'half-hourly',
  groupColumnWidth: 0,

  initWithMockData: () => {},

  isColumnReorderEnabled: false,
  isColumnReorderDragging: false,
  localColumnOrderByGroup: {},

  setPxPerMinute: (px) => set({ pxPerMinute: px }),
  setTimeIndicatorLines: (timeIndicatorLines) => set({ timeIndicatorLines }),
  setDragCreateSelection: (dragCreateSelection) => set({ dragCreateSelection }),
  setLocalColumnOrder: (groupId, columnIds) =>
    set((state) => ({ localColumnOrderByGroup: { ...state.localColumnOrderByGroup, [groupId]: columnIds } })),
  clearLocalColumnOrder: (groupId) =>
    set((state) => ({ localColumnOrderByGroup: { ...state.localColumnOrderByGroup, [groupId]: undefined } })),
  setIsColumnReorderDragging: (dragging) => set({ isColumnReorderDragging: dragging }),
  setGroupColumnWidth: (width) => set({ groupColumnWidth: width }),
  setSelectedEventId: (selectedEventId) => set({ selectedEventId }),
}));
